import React, {
  useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import CardSession from 'components/CardSession/CardSession';
import SidePanel from 'components/SidePanel/SidePanel';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import dayjs from 'dayjs';
import { getUnitIdFromNodeId } from 'utils/AICreatorHelper';
import { formatSessionChromeTitle, getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import type ICardSessionEntity from 'components/CardSession/interfaces/ICardSessionEntity';
import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';
import type ISessionsListSidebar from './interfaces/ISessionsListSidebar';

import styles from './sass/SessionsListSidebar.module.scss';

const SESSION_DATE_FORMAT = 'MMM D';

function SessionsListSidebar({
  open,
  sessions,
  allAvailableCards,
  isLoading = false,
  isError = false,
  isActionLoading = false,
  onBack,
  onClose,
  onNewSelection,
  onEditSession,
  onDeleteSession,
}: ISessionsListSidebar): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');

  const availableCardByUnitId = useMemo(() => {
    const nextAvailableCardByUnitId = new Map<number, ICardSessionEntity>();

    allAvailableCards.forEach((card) => {
      const unitId = getUnitIdFromNodeId(card.nodeId);

      if (unitId === null) {
        return;
      }

      nextAvailableCardByUnitId.set(unitId, {
        id: unitId,
        name: card.name,
        type: card.type,
      });
    });

    return nextAvailableCardByUnitId;
  }, [allAvailableCards]);

  const getSessionTitleKey = useCallback((session: ISessionVCItem): string => {
    const titleStateKey = session.isActive ? 'Current' : 'Previous';

    if (session.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE) {
      return `valueChainMap.aiSessions${titleStateKey}SupplierSearch`;
    }

    if (session.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE) {
      return `valueChainMap.aiSessions${titleStateKey}BomInvestigation`;
    }

    return `valueChainMap.aiSessions${titleStateKey}Selection`;
  }, []);

  const getSessionTitle = useCallback((session: ISessionVCItem): string => {
    const dateValue = session.updatedAt || session.createdAt;
    const formattedDate = dateValue && dayjs(dateValue).isValid()
      ? dayjs(dateValue).format(SESSION_DATE_FORMAT)
      : '';

    return formatSessionChromeTitle({
      baseTitle: t(getSessionTitleKey(session)),
      templateTitle: getSessionTemplateTitle(session),
      formattedDate,
    });
  }, [getSessionTitleKey, t]);

  const getSessionEntities = useCallback((session: ISessionVCItem): ICardSessionEntity[] => (
    (session.selectedUnits || []).map((selectedUnit) => {
      const knownCard = availableCardByUnitId.get(selectedUnit.unitId);

      if (knownCard) {
        return knownCard;
      }

      return {
        id: selectedUnit.unitId,
        name: selectedUnit.unit?.name
          || selectedUnit.name
          || selectedUnit.unitName
          || t('valueChainMap.aiSessionsFallbackEntityName', { id: selectedUnit.unitId }),
        type: selectedUnit.unit?.type || selectedUnit.type,
      };
    })
  ), [availableCardByUnitId, t]);

  const sessionCards = useMemo(() => (
    sessions
      .filter((session) => session.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE)
      .map((session) => ({
        session,
        title: getSessionTitle(session),
        entities: getSessionEntities(session),
      }))
  ), [getSessionEntities, getSessionTitle, sessions]);

  const filteredSessionCards = useMemo(() => {
    const searchLower = search.trim().toLowerCase();

    if (!searchLower) {
      return sessionCards;
    }

    return sessionCards.filter(({ title, entities }) => {
      const titleMatches = title.toLowerCase().includes(searchLower);
      const entityMatches = entities.some((entity) => entity.name.toLowerCase().includes(searchLower));

      return titleMatches || entityMatches;
    });
  }, [search, sessionCards]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t('valueChainMap.aiSessionsTitle')}
    >
      <div className={styles.header}>
        <button
          type="button"
          onClick={onBack}
          className={styles.iconButton}
          aria-label={t('common.back')}
          data-i18n-key="common.back"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </button>
        <span className={styles.headerTitle}>{t('valueChainMap.aiSessionsTitle')}</span>
        <button
          type="button"
          onClick={onClose}
          className={styles.iconButton}
          aria-label={t('common.close')}
          data-i18n-key="common.close"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles.infoBox}>
          <InfoIcon aria-hidden="true" />
          <p className={styles.description}>
            {t('valueChainMap.aiPanelDescription')}
          </p>
        </div>

        <TextField
          value={search}
          name="aiSessionsSearch"
          placeholder={t('valueChainMap.aiPanelSearchPlaceholder')}
          icon={<SearchIcon />}
          className={styles.searchField}
          onChange={({ target }) => handleSearchChange(target.value)}
        />

        <button
          type="button"
          className={styles.newSelectionButton}
          onClick={onNewSelection}
          disabled={isActionLoading}
          data-i18n-key="valueChainMap.aiSessionsNewSelection"
        >
          <PlusIcon aria-hidden="true" />
          <span>{t('valueChainMap.aiSessionsNewSelection')}</span>
        </button>

        <div className={styles.sessionsList}>
          {isLoading ? (
            <div className={styles.loaderWrapper}>
              <SmallLoader size={30} />
            </div>
          ) : null}

          {!isLoading && isError ? (
            <p className={styles.stateMessage}>{t('valueChainMap.aiSessionsLoadError')}</p>
          ) : null}

          {!isLoading && !isError && !filteredSessionCards.length ? (
            <p className={styles.stateMessage}>{t('valueChainMap.aiSessionsEmpty')}</p>
          ) : null}

          {!isLoading && !isError && filteredSessionCards.map(({ session, title, entities }) => (
            <CardSession
              key={session.id}
              title={title}
              entities={entities}
              isActive={session.isActive}
              isDisabled={isActionLoading}
              onEdit={() => onEditSession(session)}
              onDelete={() => onDeleteSession(session)}
            />
          ))}
        </div>
      </div>
    </SidePanel>
  );
}

export default SessionsListSidebar;
